<script setup>
import { useStore } from 'vuex';
import { ref, computed, onBeforeMount, onMounted, watch } from 'vue';
import { Tabs } from 'JIX/store/companyprofile.js';
import $ from 'jquery';

import DsLinkBlock from 'JIX/components/ds/DsLinkBlock.vue';
import CompanyLogoBox from 'JIX/components/CompanyLogoBox.vue';
import CompanyFollowButton from 'JIX/components/CompanyFollowButton.vue';
import CompanyRatingStars from 'JIX/components/CompanyRatingStars.vue';
import AboutCompanyprofile from 'JIX/components/CompanyProfile/AboutCompanyprofile.vue';
import JobsCompanyprofile from 'JIX/components/CompanyProfile/JobsCompanyprofile.vue';
import EvaluationsCompanyprofile from 'JIX/components/CompanyProfile/EvaluationsCompanyprofile.vue';
import TraineeCompanyprofile from 'JIX/components/CompanyProfile/TraineeCompanyprofile.vue';
import GraduateCompanyprofile from 'JIX/components/CompanyProfile/GraduateCompanyprofile.vue';
import PayCompanyprofile from 'JIX/components/CompanyProfile/PayCompanyprofile.vue';
import JobadStatisticsCompanyprofile from 'JIX/components/CompanyProfile/JobadStatisticsCompanyprofile.vue';
import NewsCompanyprofile from 'JIX/components/CompanyProfile/NewsCompanyprofile.vue';
import AvailableJobBoxes from 'JIX/components/CompanyProfile/AvailableJobBoxes.vue';

import T from './CompanyProfile.i18n.js';

const COMPANYPROFILE_EP          = 103;
const COMPANYPROFILE_GP          = 104;
const COMPANYPROFILE_HERO        = 105;
const COMPANYPROFILE_HERO_MOBILE = 106;
const COMPANYPROFILE_EP_MOBILE   = 107;
const COMPANYPROFILE_GP_MOBILE   = 108;

const store = useStore();

const root = ref();
const tabs = ref();
const tabStart = ref();

const heroSvgOptions = ref({
    space: 27,
    rows: 6,
    cols: 13,
});

const isFree = computed(() => store.state.isFree);
const heroPhotos = computed(() => store.state.heroPhotos);
const heroMobilePhotos = computed(() => store.state.heroMobilePhotos);
const heroVideos = computed(() => store.state.heroVideos);
const leftboxContent = computed(() => store.state.leftboxContent);
const evaluationData = computed(() => store.state.evaluationData);
const similarCompanyData = computed(() => store.state.similarCompanyData);
const urls = computed(() => store.state.urls);
const selectedTab = computed(() => store.state.selectedTab);
const fragmentToTab = computed(() => store.state.fragmentToTab);

const tabComponent = computed(() => {
    switch (selectedTab.value) {
    case Tabs.ABOUT:
        return AboutCompanyprofile;
    case Tabs.JOBS:
        return JobsCompanyprofile;
    case Tabs.EVALUATIONS:
        return EvaluationsCompanyprofile;
    case Tabs.TRAINEE:
        return TraineeCompanyprofile;
    case Tabs.GRADUATE:
        return GraduateCompanyprofile;
    case Tabs.PAY:
        return PayCompanyprofile;
    case Tabs.JOBADSTATISTICS:
        return JobadStatisticsCompanyprofile;
    case Tabs.NEWS:
        return NewsCompanyprofile;
    }
    throw new Error('unhandled case');
});

const promotedEvaluation = computed(() => {
    let recommended = evaluationData.value.rating_data.filter(e => e.admin_recommended !== "");
    if (recommended.length < 1) {
        recommended = evaluationData.value.rating_data.filter(e => e.comment_positive !== "" && e.rating >= 3);
    }
    return recommended[Math.floor(Math.random() * recommended.length)];
});

onBeforeMount(() => {
    determineStartTab();
});

onMounted(() => {
    const tabHeight = tabs.value.offsetHeight;
    tabStart.value.style.scrollMarginBlockStart = `${tabHeight + 30}px`;
});

watch(selectedTab, () => {
    alignTab();
});

function alignTab() {
    if (!root.value) {
        return;
    }
    const selector = "#tab-" + selectedTab.value.toLowerCase();
    const elem = root.value.querySelector(selector);
    if (elem) {
        elem.scrollIntoView({ behavior: 'smooth' });
    }
}

function determineStartTab() {
    if (window.location.hash) {
        let fragment = window.location.hash.substring(1);
        if (fragment in fragmentToTab.value) {
            return store.dispatch('selectTab', { tab: fragmentToTab.value[fragment] });
        } else if (fragment === 'show_rating') {
            return store.dispatch('selectTab', { tab: Tabs.EVALUATIONS });
        }
    }
    return store.dispatch('selectTab', { tab: 'ABOUT' });
}

function toMobileTypeid(typeid) {
    switch (typeid) {
    case COMPANYPROFILE_HERO:
        return COMPANYPROFILE_HERO_MOBILE;
    case COMPANYPROFILE_GP:
        return COMPANYPROFILE_GP_MOBILE;
    case COMPANYPROFILE_EP:
        return COMPANYPROFILE_EP_MOBILE;
    default:
        throw new Error("Unknown typeid");
    }
}

function heroTypeid() {
    switch (selectedTab.value) {
    case Tabs.TRAINEE:
        return COMPANYPROFILE_EP;
    case Tabs.GRADUATE:
        return COMPANYPROFILE_GP;
    default:
        return COMPANYPROFILE_HERO;
    }
}

function getHeros(photosRef, typeidMapping = (x) => x) {
    if (isFree.value) {
        return [];
    }
    const typeid = heroTypeid();
    const hasEP = typeidMapping(COMPANYPROFILE_EP) in photosRef.value;
    const fallbackTypeid = [COMPANYPROFILE_EP, COMPANYPROFILE_GP].includes(typeid)
        ? COMPANYPROFILE_HERO
        : hasEP ? COMPANYPROFILE_EP : COMPANYPROFILE_GP;
    return photosRef.value[typeidMapping(typeid)] || photosRef.value[typeidMapping(fallbackTypeid)] || [];
}

const heros = computed(() => getHeros(heroPhotos));
const mobileHeros = computed(() => getHeros(heroMobilePhotos, toMobileTypeid));
const videoHero = computed(() => getHeros(heroVideos)[0]);

function selectTab(tab) {
    store.dispatch('selectTab', { tab });
    tabStart.value.scrollIntoView({ block: "nearest" });
    $('.carousel').carousel(0);
}

function jixAnalytics(label, action) {
    store.dispatch('jixAnalytics', { label, action });
}
</script>

<template>
    <div ref="root">
        <div class="row">
            <div class="col-md-3">
                <div class="vp-card mb-3">
                    <div class="vp-card__header">
                        <CompanyLogoBox size="sm" :logo-url="leftboxContent ? leftboxContent.logoUrl : null" />
                        <div class="vp-card__follow">
                            <span class="vp-card__follow-count">{{ T.followersCount(leftboxContent.followerCount) }}</span>
                            <CompanyFollowButton
                                :companyid="store.state.companyId"
                                :following="store.state.following"
                                @click="jixAnalytics('følg', 'klik-venstre-navigation')"
                                @update="$store.commit('setFollowing', $event)"
                            />
                        </div>
                    </div>
                    <h1 class="vp-card__name">
                        {{ store.state.companyName }}
                    </h1>
                    <div
                        v-if="evaluationData.enable_ratings === 1 && evaluationData.hide_ratings !== 1"
                        class="vp-card__data"
                    >
                        <a
                            class="vp-card__ratings"
                            :href="urls.EVALUATIONS"
                            @click.prevent="selectTab('EVALUATIONS')"
                        >
                            <CompanyRatingStars
                                :rating="evaluationData.question2finalrating[0]"
                                size="sm"
                            />
                            <div class="vp-card__ratings-count">
                                {{ T.evaluationsCount(evaluationData.unique_ratings_cnt) }}
                            </div>
                        </a>
                    </div>
                </div>

                <div v-if="promotedEvaluation !== undefined" class="d-none d-md-block" :class="{ 'mb-4' : !isFree }">
                    <div class="mb-3 bg-white border">
                        <div class="p-3">
                            <h2 class="h5 mb-0">
                                {{ T.highlightedRating() }}
                            </h2>
                            <blockquote class="blockquote-small">
                                <p>
                                    {{ promotedEvaluation.comment_positive }}
                                </p>
                                <footer>
                                    <CompanyRatingStars
                                        class="mb-1"
                                        :rating="promotedEvaluation.rating"
                                        size="sm"
                                    />
                                    <p>
                                        {{ T.statusName(promotedEvaluation.still_employed) }}
                                    </p>
                                </footer>
                            </blockquote>
                        </div>
                        <hr class="m-0">
                        <DsLinkBlock
                            :href="urls.EVALUATIONS"
                            @click.prevent="jixAnalytics('se-evalueringer', 'klik-venstre-navigation');selectTab('EVALUATIONS')"
                            link-class="justify-content-center py-2 w-100 text-sm"
                        >
                            {{ T.seeEvaluations() }}
                        </DsLinkBlock>
                    </div>
                </div>

                <AvailableJobBoxes class="d-none d-md-block" @job-tab="selectTab('JOBS')" />

                <div v-if="isFree && similarCompanyData !== null" class="d-none d-md-block">
                    <span class="text-xs">
                        {{ T.similarCompany() }}
                    </span>
                    <div class="mb-3 bg-white border mt-2 p-3">
                        <div class="d-flex justify-content-between">
                            <a :href="similarCompanyData.profile_url" class="text-decoration-none">
                                <CompanyLogoBox :logo-url="similarCompanyData.logoUrl" class="mr-3 mr-md-4" size="sm" />
                            </a>
                            <div
                                class="d-flex align-items-start"
                                @click="jixAnalytics('lignende-virksomheder-følg', 'klik-venstre-navigation')"
                                v-html="similarCompanyData.followButton"
                            />
                        </div>
                        <div class="mt-2">
                            <a
                                @click="jixAnalytics('lignende-virksomheder-link', 'klik-venstre-navigation')"
                                :href="similarCompanyData.profile_url"
                                class="text-decoration-none"
                            >
                                <b> {{ similarCompanyData.companyname }} </b>
                            </a>
                        </div>
                        <hr>
                        <div class="mt-3 d-flex text-sm" v-if="similarCompanyData.rating_count > 0">
                            <span class="text-muted mr-auto">
                                {{ T.evaluationsCount(similarCompanyData.rating_count) }}
                            </span>
                            <CompanyRatingStars
                                :rating="similarCompanyData.rating"
                                size="inline"
                            />
                        </div>
                        <div class="d-flex mt-2">
                            <span class="text-muted mr-auto text-sm">
                                {{ T.jobads() }}
                            </span>
                            <a :href="similarCompanyData.job_search_url" class="text-decoration-none">
                                {{ similarCompanyData.job_count }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-md-9 order-1 order-md-2">
                <div
                    v-if="videoHero"
                    v-html="videoHero"
                />
                <div
                    v-else
                    class="text-center"
                >
                    <template
                        v-if="heros.length"
                    >
                        <div
                            :class="[ 'carousel', 'slide', mobileHeros.length ? { 'd-none': true, 'd-md-block': true } : {} ]"
                            data-ride="carousel"
                        >
                            <div class="carousel-inner">
                                <div
                                    v-for="(src, index) in heros"
                                    :key="index"
                                    :class="{ 'carousel-item': true, 'active': index === 0 }"
                                >
                                    <img
                                        class="w-100 text-brand heroHeight"
                                        role="presentation"
                                        alt=""
                                        :src="src"
                                    >
                                </div>
                            </div>
                        </div>
                        <div
                            v-if="mobileHeros.length"
                            class="carousel slide d-md-none"
                            data-ride="carousel"
                        >
                            <div class="carousel-inner">
                                <div
                                    v-for="(src, index) in mobileHeros"
                                    :key="index"
                                    :class="{ 'carousel-item': true, 'active': index === 0 }"
                                >
                                    <img
                                        class="w-100 text-brand heroHeight"
                                        role="presentation"
                                        alt=""
                                        :src="src"
                                    >
                                </div>
                            </div>
                        </div>
                    </template>
                    <div class="w-100 bg-brand text-brand d-none d-md-block" v-else>
                        <svg
                            class="ml-auto heroSvg"
                            role="presentation"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 387 198"
                        >
                            <defs>
                                <symbol id="vp-dot-row" viewBox="0 0 387 198" fill="#FFF">
                                    <circle
                                        :cx="4.5 + i * heroSvgOptions.space"
                                        cy="4.5"
                                        r="4.5"
                                        v-for="i in Array(heroSvgOptions.cols).keys()"
                                        :key="i"
                                    />
                                </symbol>
                            </defs>
                            <use
                                xmlns:xlink="http://www.w3.org/1999/xlink"
                                xlink:href="#vp-dot-row"
                                :transform="'translate(27 ' + ((i +1) * heroSvgOptions.space) + ')'"
                                v-for="i in Array(heroSvgOptions.rows).keys()"
                                :key="i"
                            />
                        </svg>
                    </div>
                </div>
                <div id="tabs">
                    <ul
                        class="nav nav-tabs-sm mb-md-4 sticky-top p-3 bg-white border horizontal-scroll flex-nowrap mb-4 column-gap-4"
                        role="tablist"
                        ref="tabs"
                    >
                        <li
                            v-for="(tab, index) in store.state.activeTabs"
                            :key="index"
                            class="nav-item"
                        >
                            <a
                                :class="[ 'nav-link', 'text-sm', { active: selectedTab === tab } ]"
                                :href="urls[tab]"
                                @click.prevent="selectTab(tab)"
                                role="tab"
                                :aria-selected="selectedTab === tab ? 'true' : 'false'"
                                aria-controls=""
                                :id="'tab-' + tab.toLowerCase()"
                            >
                                {{ T.tabName(tab) }}
                            </a>
                        </li>
                    </ul>
                    <div ref="tabStart" />
                    <KeepAlive>
                        <Component
                            id="companyprofile-tab-content"
                            :is="tabComponent"
                            ref="tabInstance"
                            @select-tab="selectTab"
                            role="tabpanel"
                            @job-tab="selectTab('JOBS')"
                        />
                    </KeepAlive>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.similarCompanyNoLogo {
    height: 80px;
    width: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    > img {
        max-height: 100%;
        max-width: 100%;
    }
}

.horizontal-scroll {
    overflow-x: auto;
    white-space: nowrap;
}

.heroHeight {
    min-height: 200px;
    max-height: 400px;
    overflow: hidden;
    object-fit: cover;
    object-position: center center;
}

.heroSvg {
    display: block;
    height: 200px;
}

.smallLogo {
    height: 50px;
    width: 50px;
}
</style>
